import React, { useState, useEffect, useRef } from 'react';
import './Projects.css';

function Projects() {
  const [activeFilter, setActiveFilter] = useState('fashion');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [visibleProjects, setVisibleProjects] = useState([0, 1, 2, 3, 4, 5]);
  const projectsRef = useRef([]);
  const sliderRef = useRef(null);

  const projects = [
    {
      id: 1,
      title: 'Luxe Fashion',
      category: 'fashion',
      description: 'Premium fashion brand transformation',
      image: '/images/fashion.png',
      results: ['45% conversion increase', '60% mobile sales growth', '$200K monthly revenue'],
      tags: ['Redesign', 'Mobile-First', 'Conversion Optimization']
    },
    {
      id: 2,
      title: 'TechGear Pro',
      category: 'electronics',
      description: 'Modern electronics retail experience',
      image: '/images/tech.png',
      results: ['1000+ products optimized', '$50K first month', '4.8/5 satisfaction'],
      tags: ['New Store', 'Custom Features', 'UX Design']
    },
    {
      id: 3,
      title: 'Organic Beauty Co.',
      category: 'beauty',
      description: 'Clean beauty brand launch',
      image: '/images/health.png',
      results: ['250% ROI in 6 months', '90% retention rate', '5-star average'],
      tags: ['Brand Launch', 'E-commerce', 'SEO']
    },
    {
      id: 4,
      title: 'FitLife Nutrition',
      category: 'health',
      description: 'Health supplement store optimization',
      image: '/images/FitLife.png',
      results: ['70% faster load times', '55% mobile conversion increase', 'Top 10 Google ranking'],
      tags: ['Performance', 'Optimization', 'Mobile']
    },
    {
      id: 5,
      title: 'Home Decor Haven',
      category: 'home',
      description: 'Luxury home decor experience',
      image: '/images/HomeDecor.png',
      results: ['42% conversion increase', '3x social engagement', '50% more page views'],
      tags: ['Redesign', 'Visual Design', 'UX']
    },
    {
      id: 6,
      title: 'Artisan Coffee Co.',
      category: 'food',
      description: 'Subscription coffee platform',
      image: '/images/coffee.png',
      results: ['500+ active subscribers', '$25K MRR', '85% retention rate'],
      tags: ['Subscriptions', 'Custom Dev', 'Integration']
    }
  ];

  const filters = [
    { id: 'fashion', label: 'Fashion' },
    { id: 'electronics', label: 'Electronics' },
    { id: 'beauty', label: 'Beauty' },
    { id: 'health', label: 'Health' },
    { id: 'home', label: 'Interior' },
    { id: 'food', label: 'Food & Beverage' }
  ];

  // Get active filter index and determine adjacent filters
  const activeIndex = filters.findIndex(filter => filter.id === activeFilter);
  const prevIndex = activeIndex > 0 ? activeIndex - 1 : filters.length - 1;
  const nextIndex = activeIndex < filters.length - 1 ? activeIndex + 1 : 0;
  const prevCategory = filters[prevIndex]?.id;
  const nextCategory = filters[nextIndex]?.id;

  // Show all projects, but categorize them for blur effects
  const allProjects = projects.map(project => {
    let cardClass = '';
    if (project.category === activeFilter) {
      cardClass = 'active';
    } else if (project.category === prevCategory) {
      cardClass = 'prev';
    } else if (project.category === nextCategory) {
      cardClass = 'next';
    } else {
      cardClass = 'far';
    }
    return { ...project, cardClass };
  });

  // Scroll slider to center active category cards (horizontal only)
  useEffect(() => {
    if (sliderRef.current) {
      // Small delay to ensure DOM has updated with new active classes
      const scrollTimeout = setTimeout(() => {
        // Find the index of the active project based on category
        const activeProjectIndex = projects.findIndex(p => p.category === activeFilter);
        const activeCard = projectsRef.current[activeProjectIndex];

        if (activeCard && sliderRef.current) {
          setIsTransitioning(true);

          // Calculate horizontal scroll position to center the active card
          const slider = sliderRef.current;
          const cardLeft = activeCard.offsetLeft;
          const cardWidth = activeCard.offsetWidth;
          const sliderWidth = slider.offsetWidth;
          const scrollPosition = cardLeft - (sliderWidth / 2) + (cardWidth / 2);

          // Scroll horizontally only (prevents page from jumping to this section)
          slider.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          });

          // Clear transition state after scroll completes
          setTimeout(() => {
            setIsTransitioning(false);
          }, 600);
        }
      }, 50);

      return () => clearTimeout(scrollTimeout);
    }
  }, [activeFilter, projects]);

  // Animate projects when filter changes
  useEffect(() => {
    setIsTransitioning(true);
    
    // Don't clear visibleProjects - keep them visible to prevent flashing
    setTimeout(() => {
      setIsTransitioning(false);
      // Ensure all projects remain visible (no flashing)
      const allIndices = allProjects.map((_, index) => index);
      setVisibleProjects(allIndices);
    }, 50); // Minimal delay
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  // Navigate to previous category
  const handlePrevious = () => {
    const newIndex = activeIndex > 0 ? activeIndex - 1 : filters.length - 1;
    setActiveFilter(filters[newIndex].id);
  };

  // Navigate to next category
  const handleNext = () => {
    const newIndex = activeIndex < filters.length - 1 ? activeIndex + 1 : 0;
    setActiveFilter(filters[newIndex].id);
  };

  // Auto-scroll slider every 5 seconds (continuous) - disabled on mobile
  useEffect(() => {
    // Disable auto-scroll on mobile devices for better touch experience
    const isMobile = window.innerWidth <= 640;
    if (isMobile) return;

    const autoScrollInterval = setInterval(() => {
      const currentIndex = filters.findIndex(filter => filter.id === activeFilter);
      const newIndex = currentIndex < filters.length - 1 ? currentIndex + 1 : 0;
      setActiveFilter(filters[newIndex].id);
    }, 5000);

    return () => clearInterval(autoScrollInterval);
  }, [activeFilter, filters]);

  // Parallax effect on project images (only for active cards)
  useEffect(() => {
    const handleScroll = () => {
      projectsRef.current.forEach((card) => {
        if (card && card.classList.contains('active')) {
          const rect = card.getBoundingClientRect();
          const scrollProgress = (window.innerHeight - rect.top) / window.innerHeight;

          if (scrollProgress > 0 && scrollProgress < 1) {
            const image = card.querySelector('.project-image-inner');
            if (image) {
              const translateY = (scrollProgress - 0.5) * -30;
              image.style.transform = `translateY(${translateY}px) scale(1.1)`;
            }
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeFilter]);

  return (
    <section id="projects" className="projects-section">
      <div className="container">
        <div className="section-header fade-in">
          <div className="section-badge">Our Work</div>
          <h2 className="section-title">
            Featured Projects That
            <span className="gradient-text"> Drive Results</span>
          </h2>
          <p className="section-description">
            Explore our portfolio of successful Shopify stores. Each project represents
            our commitment to delivering exceptional design and measurable business growth.
          </p>
        </div>

        <div className="projects-filters fade-in">
          {filters.map(filter => (
            <button
              key={filter.id}
              className={`filter-btn ${activeFilter === filter.id ? 'active' : ''}`}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className={`projects-slider-wrapper ${isTransitioning ? 'transitioning' : ''}`}>
          <button 
            className="slider-nav-btn slider-nav-prev"
            onClick={handlePrevious}
            aria-label="Previous projects"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <div className="projects-slider" ref={sliderRef}>
            {allProjects.map((project, index) => (
              <div
                key={project.id}
                ref={el => projectsRef.current[index] = el}
                className={`project-card ${project.cardClass} ${visibleProjects.includes(index) ? 'visible' : ''}`}
              >
              <div className={`project-image ${project.image.includes('HomeDecor') ? 'homedecor-image-container' : ''} ${project.image.includes('FitLife') ? 'fitlife-image-container' : ''}`}>
                {project.image.startsWith('linear-gradient') || project.image.startsWith('radial-gradient') ? (
                  <div
                    className="project-image-inner"
                    style={{ background: project.image }}
                  >
                    <div className="project-image-overlay"></div>
                  </div>
                ) : (
                  <div className={`project-image-inner has-image ${project.image.includes('HomeDecor') ? 'homedecor-image' : ''} ${project.image.includes('FitLife') ? 'fitlife-image' : ''}`}>
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className={`project-image-img ${project.image.includes('HomeDecor') ? 'homedecor-img' : ''} ${project.image.includes('FitLife') ? 'fitlife-img' : ''}`}
                    />
                    <div className="project-image-overlay"></div>
                  </div>
                )}
                <div className="project-overlay">
                  <div className="project-tags">
                    {project.tags.map((tag, idx) => (
                      <span key={idx} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="project-content">
                <h3 className="project-title">{project.title}</h3>
                <p className="project-description">{project.description}</p>
              </div>
            </div>
          ))}
          </div>
          
          <button 
            className="slider-nav-btn slider-nav-next"
            onClick={handleNext}
            aria-label="Next projects"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}

export default Projects;
